<template>
  <div class="table-container">
    <svg style="display: none">
      <filter
          id="glass-distortion"
          x="0%"
          y="0%"
          width="100%"
          height="100%"
          filterUnits="objectBoundingBox"
      >
        <feTurbulence
            type="fractalNoise"
            baseFrequency="0.001 0.005"
            numOctaves="1"
            seed="17"
            result="turbulence"
        />
        <!-- Liked Seeds: 5, 14, 17 -->

        <feComponentTransfer in="turbulence" result="mapped">
          <feFuncR type="gamma" amplitude="1" exponent="10" offset="0.5" />
          <feFuncG type="gamma" amplitude="0" exponent="1" offset="0" />
          <feFuncB type="gamma" amplitude="0" exponent="1" offset="0.5" />
        </feComponentTransfer>

        <feGaussianBlur in="turbulence" stdDeviation="3" result="softMap" />

        <feSpecularLighting
            in="softMap"
            surfaceScale="5"
            specularConstant="1"
            specularExponent="100"
            lighting-color="white"
            result="specLight"
        >
          <fePointLight x="-200" y="-200" z="300" />
        </feSpecularLighting>

        <feComposite
            in="specLight"
            operator="arithmetic"
            k1="0"
            k2="1"
            k3="1"
            k4="0"
            result="litImage"
        />

        <feDisplacementMap
            in="SourceGraphic"
            in2="softMap"
            scale="200"
            xChannelSelector="R"
            yChannelSelector="G"
        />
      </filter>
    </svg>
    <!-- Loading state -->
    <div v-if="loading" class="loading-container">
      <div class="loading-spinner"></div>
      <div class="loading-text">Loading data...</div>
    </div>

    <!-- Empty state -->
    <div v-else-if="!internalData || internalData.length === 0" class="no-data-message">
      No meters found matching your criteria, please use filters.
    </div>

    <!-- Table with data -->
    <template v-else>
      <div class="meters-table">
        <!-- Table Header -->
        <div class="table-header">
          <div
              v-for="column in columns"
              :key="column.id"
              class="table-cell"
              @click="handleSort(column.id)"
              :class="{ 'sortable': column.sortable, 'sorted': sortColumn === column.id }"
          >
            {{ column.label }}
            <span v-if="sortColumn === column.id" class="sort-indicator">
              {{ sortDirection === 'asc' ? '▲' : '▼' }}
            </span>
          </div>
        </div>

        <!-- Table Rows -->
        <div
            class="table-row"
            v-for="(item, index) in internalData"
            :key="getRowKey(item, index)"
            @click="handleRowSelect(item)"
            @mouseenter="handleRowHover(item, true)"
            @mouseleave="handleRowHover(item, false)"
            :class="{ 'selected': selectedRow && selectedRow[rowKey] === item[rowKey] }"
        >
          <div class="liquidGlass-effect"></div>
          <div class="liquidGlass-tint"></div>
          <div class="liquidGlass-shine"></div>
          <div v-for="column in columns" :key="column.id" class="table-cell liquidGlass-text">
            <!-- If diagnostic column, forcibly show "with alarms" -->
            <template v-if="column.id === 'diagnostic'">
        <span class="badge" :class="getBadgeClass(item)">
          {{ getDiagnostics(item) }}
        </span>
            </template>
            <!-- Otherwise use the slot or default rendering -->
            <template v-else>
              <slot :name="column.id" :item="item" :column="column">
                <span
                    :class="{ 'clickable-ldn': column.id === 'ldn' }"
                    @click="column.id === 'ldn' ? navigateToDeviceDetails($event, item) : null"
                >
                  {{ renderCellContent(item, column) }}
                </span>
              </slot>
            </template>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="pagination" class="pagination-controls">
        <div class="pagination-info">
          Showing {{ startItem }}-{{ endItem }} of {{ totalItems }} items
        </div>
        <div class="pagination-buttons">
          <button
              class="pagination-button"
              :disabled="currentPage === 1"
              @click="handlePageChange(currentPage - 1)"
          >
            Previous
          </button>

          <div class="page-numbers">
            <button
                v-for="page in visiblePageNumbers"
                :key="page"
                :class="['page-number', { active: currentPage === page }]"
                @click="handlePageChange(page)"
            >
              {{ page }}
            </button>
          </div>

          <button
              class="pagination-button"
              :disabled="currentPage === totalPages"
              @click="handlePageChange(currentPage + 1)"
          >
            Next
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { getAlarmStatus } from '@/services/alarmHelper';

export default {
  name: 'DevicesTable',
  props: {
    // Data to display in table
    data: {
      type: Array,
      required: true
    },

    // Column configuration
    columns: {
      type: Array,
      required: true
    },

    // Unique identifier for rows (used for selection)
    rowKey: {
      type: String,
      default: 'id'
    },

    // Loading state
    loading: {
      type: Boolean,
      default: false
    },

    // Initial sort configuration
    initialSortColumn: {
      type: String,
      default: ''
    },

    initialSortDirection: {
      type: String,
      default: 'asc',
      validator: (value) => ['asc', 'desc'].includes(value)
    },

    // Pagination configuration
    pagination: {
      type: Boolean,
      default: true
    },

    currentPage: {
      type: Number,
      default: 1
    },

    pageSize: {
      type: Number,
      default: 10
    },

    totalItems: {
      type: Number,
      default: 0
    },

    // Selected row
    selectedRow: {
      type: Object,
      default: null
    },

    // Sort configuration (external)
    sortColumn: {
      type: String,
      default: ''
    },

    sortDirection: {
      type: String,
      default: 'asc'
    }
  },

  data() {
    return {
      // Internal data copy that we'll sort
      internalData: []
    };
  },

  watch: {
    // Update internal data when prop data changes
    data: {
      immediate: true,
      handler(newData) {
        this.internalData = JSON.parse(JSON.stringify(newData || []));
      }
    }
  },

  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.totalItems / this.pageSize));
    },

    startItem() {
      return this.internalData.length === 0 ? 0 : (this.currentPage - 1) * this.pageSize + 1;
    },

    endItem() {
      return Math.min(this.startItem + this.internalData.length - 1, this.totalItems);
    },

    visiblePageNumbers() {
      const maxVisiblePages = 5;
      const halfVisible = Math.floor(maxVisiblePages / 2);

      let startPage = Math.max(1, this.currentPage - halfVisible);
      let endPage = Math.min(this.totalPages, startPage + maxVisiblePages - 1);

      if (endPage === this.totalPages) {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }

      const pages = [];
      for (let i = startPage; i <= endPage; i++) {
        pages.push(i);
      }
      return pages;
    }
  },

  methods: {
    handleSort(columnId) {
      this.$emit('sort', { column: columnId, direction: this.sortColumn === columnId && this.sortDirection === 'asc' ? 'desc' : 'asc' });
    },

    handleRowSelect(row) {
      this.$emit('row-select', row);
    },

    handleRowHover(item, isHovering) {
      // Determine device type for 3D model mapping
      let deviceType = (item.deviceType || item.type || '').toLowerCase();
      let caliber = item.caliber || '';

      // Emit hover events for 3D animation coordination
      this.$emit('row-hover', {
        device: item,
        isHovering: isHovering,
        deviceType: deviceType,
        caliber: caliber,
        ldn: item.ldn || item.logical_device_number
      });
    },

    navigateToDeviceDetails(event, item) {
      event.stopPropagation();
      const deviceId = item.ldn || item.logical_device_number || '';
      if (deviceId) {
        this.$router.push(`/devices/${deviceId}`);
      }
    },

    handlePageChange(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.$emit('page-change', page);
      }
    },

    renderCellContent(item, column) {
      if (!item || !column) return '';

      let value;
      if (column.id === 'ldn') {
        value = item.ldn || item.logical_device_number || '';
      } else if (column.id === 'deviceType') {
        value = item.deviceType || item.type || '';
      } else {
        value = this.getValueByPath(item, column.id);
      }

      if (column.formatter && typeof column.formatter === 'function') {
        return column.formatter(value, item);
      }

      if (column.type === 'date' && value) {
        return this.formatDate(value);
      }
      return value || column.default || '';
    },

    getValueByPath(object, path) {
      if (!object || !path) return '';
      try {
        return path.split('.').reduce((o, p) => (o ? o[p] : ''), object);
      } catch (error) {
        console.error(`Error accessing path ${path}:`, error);
        return '';
      }
    },

    getRowKey(item, index) {
      if (item && this.rowKey && item[this.rowKey] !== undefined) {
        return item[this.rowKey];
      }
      return index;
    },
    getDiagnostics(item) {
      const status = getAlarmStatus(item);
      if (!status.hasAlarms) {
        return 'NO ALARMS';
      }
      return status.alarms.length > 0
          ? status.alarms.join(', ')
          : 'ALARM';
    },

    getBadgeClass(item) {
      const status = getAlarmStatus(item);
      return status.hasAlarms ? 'badge-error' : 'badge-ok';
    },

    formatDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;

        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        return `${hours}:${minutes} ${day}/${month}/${year}`;
      } catch (error) {
        console.error('Error formatting date:', error);
        return dateString;
      }
    }
  }
};
</script>

<style scoped>
.table-container {
  position: relative;
  min-height: 300px;
}

/* Loading indicator */
.loading-container {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top: 3px solid rgba(255, 255, 255, 0.8);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.loading-text {
  font-size: 14px;
  opacity: 0.7;
}

/* No data message */
.no-data-message {
  text-align: center;
  padding: 40px 0;
  font-size: 14px;
  opacity: 0.7;
}

/* Meters Table */
.meters-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 24px;
}

.table-header {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  padding: 10px 0;
  font-size: 11px;
  font-weight: 500;
  letter-spacing: 0.05em;
  text-transform: uppercase;
}

.table-header .table-cell {
  cursor: default;
}

.table-header .table-cell.sortable {
  cursor: pointer;
  position: relative;
}

.table-header .table-cell.sortable:hover {
  opacity: 0.8;
}

.sort-indicator {
  margin-left: 4px;
  font-size: 10px;
}

/* Table Row with Liquid Glass Effect */
.table-row {
  display: flex;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  padding: 16px 0;
  font-size: 11px;
  position: relative;
  overflow: hidden;
  cursor: pointer;
}

/* Liquid Glass Effect Elements - Hidden by default */
.liquidGlass-effect,
.liquidGlass-tint,
.liquidGlass-shine {
  position: absolute;
  inset: 0;
  opacity: 0;
  transition: opacity 0.4s cubic-bezier(0.175, 0.885, 0.32, 2.2);
  pointer-events: none;
}

.liquidGlass-effect {
  z-index: 0;
  backdrop-filter: blur(3px);
  filter: url(#glass-distortion);
  isolation: isolate;
}

.liquidGlass-tint {
  z-index: 1;
  background: rgba(255, 255, 255, 0.04);
}

.liquidGlass-shine {
  z-index: 2;
}

/* Apply liquid glass effect on hover and selected states */
.table-row:hover,
.table-row.selected {
  box-shadow: 0 3px 3px rgba(0, 0, 0, 0.02), 0 0 20px rgba(0, 0, 0, 0.05);
}

/* Show glass effect elements on hover and selected */
.table-row:hover .liquidGlass-effect,
.table-row:hover .liquidGlass-tint,
.table-row:hover .liquidGlass-shine,
.table-row.selected .liquidGlass-effect,
.table-row.selected .liquidGlass-tint,
.table-row.selected .liquidGlass-shine {
  opacity: 1;
}

/* Table Cell Styling */
.table-cell {
  flex: 1;
  padding: 0 8px;
  position: relative;
  z-index: 3;
}

/* Text color for liquid glass effect */
.liquidGlass-text {
  color: inherit;
}

.table-row:hover .liquidGlass-text,
.table-row.selected .liquidGlass-text {
  color: white;
}

.table-cell {
  flex: 1;
  padding: 0 8px;
}

/* Pagination controls */
.pagination-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 24px;
  padding: 16px 0;
}

.pagination-info {
  font-size: 11px;
  opacity: 0.7;
}

.pagination-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

.pagination-button {
  background: none;
  border: none;
  color: white;
  font-size: 11px;
  padding: 4px 12px;
  border-radius: 4px;
  cursor: pointer;
  opacity: 0.7;
  transition: all 0.2s;
}

.pagination-button:hover:not(:disabled) {
  background-color: rgba(255, 255, 255, 0.1);
  opacity: 1;
}

.pagination-button:disabled {
  cursor: not-allowed;
  opacity: 0.3;
}

.page-numbers {
  display: flex;
  gap: 4px;
}

.page-number {
  background: none;
  border: none;
  color: white;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  transition: all 0.2s;
}

.page-number:hover:not(.active) {
  background-color: rgba(255, 255, 255, 0.1);
}

.page-number.active {
  background-color: rgba(255, 255, 255, 0.2);
  font-weight: 600;
}

.clickable-ldn {
  color: #4fc3f7;
  cursor: pointer;
  text-decoration: none;
}
.badge {
  display: inline-block;
  padding: 4px 10px;
  background: rgba(74, 222, 128, 0.1);
  color: #4ade80;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
}

.badge-ok {
  background: rgba(74, 222, 128, 0.1);
  color: #4ade80;
}

.badge-error {
  background: rgba(248, 113, 113, 0.1);
  color: #f87171;
}
</style>